import { useState, useMemo, useCallback, useEffect } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { usePayPeriods } from '@/common/hooks/useTimesheets'
import { useAuth } from '@/common/hooks/useAuth'
import { useImpersonation } from '@/common/hooks/useImpersonation'
import { getAllowedPeriods, getDefaultPeriod } from '@/common/utils/payPeriodNavigation'
import type { AdminTimesheetSummary, AdminPayPeriodDetail, EmployeeTimesheetStatus } from '@/common/hooks/useAdminTimesheetSummary'
import * as styles from './PayPeriodOverview.css'

export interface PayPeriodOverviewProps {
  title: string
  useData: (startDate?: string) => UseQueryResult<AdminTimesheetSummary>
  enableGrouping?: boolean
  onEmployeeClick?: (emp: EmployeeTimesheetStatus) => void
  renderActions?: (payPeriod: AdminPayPeriodDetail) => React.ReactNode
}

function formatPeriodLabel(start: string, end: string): string {
  const s = new Date(start + 'T00:00:00')
  const e = new Date(end + 'T00:00:00')
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  const yearOpts: Intl.DateTimeFormatOptions = { ...opts, year: 'numeric' }
  if (s.getFullYear() === e.getFullYear()) {
    return `${s.toLocaleDateString('en-US', opts)} - ${e.toLocaleDateString('en-US', yearOpts)}`
  }
  return `${s.toLocaleDateString('en-US', yearOpts)} - ${e.toLocaleDateString('en-US', yearOpts)}`
}

function getStateBadgeClass(state: string): string {
  switch (state) {
    case 'active': return styles.stateActive
    case 'drafting': return styles.stateDrafting
    case 'closed': return styles.stateClosed
    default: return styles.stateBadge
  }
}

function getStatusBadgeClass(status: string): string {
  switch (status) {
    case 'submitted': return styles.statusSubmitted
    case 'approved': return styles.statusApproved
    case 'draft': return styles.statusDraft
    case 'rejected': return styles.statusRejected
    case 'missing': return styles.statusMissing
    default: return styles.statusBadge
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

interface ManagerGroup {
  managerId: number | null
  managerName: string
  employees: EmployeeTimesheetStatus[]
  counts: Record<string, number>
}

function formatGroupCounts(counts: Record<string, number>): string {
  const parts: string[] = []
  for (const status of ['submitted', 'approved', 'draft', 'rejected', 'missing']) {
    const count = counts[status]
    if (count > 0) parts.push(`${count} ${status}`)
  }
  return parts.join(', ')
}

function buildManagerGroups(employees: EmployeeTimesheetStatus[]): ManagerGroup[] | null {
  const hasAnyManager = employees.some((e) => e.manager_id != null)
  if (!hasAnyManager) return null

  const groupMap = new Map<number | null, ManagerGroup>()

  for (const emp of employees) {
    const key = emp.manager_id
    let group = groupMap.get(key)
    if (!group) {
      group = {
        managerId: key,
        managerName: emp.manager_name ?? 'No Manager',
        employees: [],
        counts: {},
      }
      groupMap.set(key, group)
    }
    group.employees.push(emp)
    group.counts[emp.status] = (group.counts[emp.status] ?? 0) + 1
  }

  // Sort: named managers alphabetically, "No Manager" last
  return Array.from(groupMap.values()).sort((a, b) => {
    if (a.managerId == null && b.managerId != null) return 1
    if (a.managerId != null && b.managerId == null) return -1
    return a.managerName.localeCompare(b.managerName)
  })
}

export function PayPeriodOverview({ title, useData, enableGrouping = true, onEmployeeClick, renderActions }: PayPeriodOverviewProps) {
  const { user } = useAuth()
  const { isImpersonating, impersonatedRoles } = useImpersonation()
  const roles = useMemo(
    () => (isImpersonating ? impersonatedRoles : (user?.roles ?? [])),
    [isImpersonating, impersonatedRoles, user?.roles],
  )
  const { data: periods } = usePayPeriods()
  const [selectedStartDate, setSelectedStartDate] = useState<string | undefined>(undefined)
  const [defaultApplied, setDefaultApplied] = useState(false)
  const { data, isLoading, error, refetch } = useData(selectedStartDate)
  const [expandedGroups, setExpandedGroups] = useState<Set<number | null>>(new Set())

  // Role-aware period restriction
  const { previous, current, isRestricted } = useMemo(
    () => (periods ? getAllowedPeriods(periods, roles) : { previous: null, current: null, isRestricted: false }),
    [periods, roles],
  )

  // Apply default period on initial load for restricted users
  useEffect(() => {
    if (defaultApplied || !isRestricted) return
    if (!previous && !current) return
    const defaultPeriod = getDefaultPeriod(previous, current)
    if (defaultPeriod) {
      setSelectedStartDate(defaultPeriod.start_date)
      setDefaultApplied(true)
    }
  }, [defaultApplied, isRestricted, previous, current])

  // Find the current period index in the periods list for navigation (admin only)
  const currentPeriodIndex = useMemo(() => {
    if (!periods || !data) return -1
    return periods.findIndex(
      (p) => p.start_date === data.pay_period.start_date && p.end_date === data.pay_period.end_date,
    )
  }, [periods, data])

  // Find the default period index (previous pay period = the one the API returns by default)
  const defaultPeriodIndex = useMemo(() => {
    if (!periods) return -1
    if (selectedStartDate === undefined && data) {
      return periods.findIndex(
        (p) => p.start_date === data.pay_period.start_date && p.end_date === data.pay_period.end_date,
      )
    }
    return -1
  }, [periods, data, selectedStartDate])

  // Memoize the max navigable index (one past the default period = current period)
  const maxIndex = useMemo(() => {
    if (defaultPeriodIndex >= 0 && periods) return Math.min(defaultPeriodIndex + 1, periods.length - 1)
    if (!periods) return -1
    return periods.length - 2
  }, [defaultPeriodIndex, periods])

  const managerGroups = useMemo(() => {
    if (!enableGrouping || !data) return null
    return buildManagerGroups(data.employees)
  }, [enableGrouping, data])

  const canGoBack = currentPeriodIndex > 0
  const canGoForward = currentPeriodIndex >= 0 && currentPeriodIndex < maxIndex

  const handlePrev = () => {
    if (!periods || currentPeriodIndex <= 0) return
    const prevPeriod = periods[currentPeriodIndex - 1]
    setSelectedStartDate(prevPeriod.start_date)
    setExpandedGroups(new Set())
  }

  const handleNext = () => {
    if (!periods || !canGoForward) return
    const nextPeriod = periods[currentPeriodIndex + 1]
    setSelectedStartDate(nextPeriod.start_date)
    setExpandedGroups(new Set())
  }

  const toggleGroup = useCallback((managerId: number | null) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(managerId)) {
        next.delete(managerId)
      } else {
        next.add(managerId)
      }
      return next
    })
  }, [])

  if (isLoading) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>{title}</h1>
        <div className={styles.loading}>Loading...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>{title}</h1>
        <div className={styles.errorBox}>
          <span>Failed to load data: {error.message}</span>
          <button onClick={() => refetch()}>Retry</button>
        </div>
      </div>
    )
  }

  if (!data) return null

  const { pay_period, summary, employees } = data
  const needsAction = summary.draft + summary.missing + summary.rejected

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Pay period overview</h1>

      {/* Pay period navigation */}
      {isRestricted ? (
        <div className={styles.periodNav}>
          {previous && (
            <button
              className={`${styles.navArrow} ${pay_period.start_date === previous.start_date ? styles.navArrowActive : ''}`}
              onClick={() => { setSelectedStartDate(previous.start_date); setExpandedGroups(new Set()) }}
              style={{ width: 'auto', padding: '4px 12px', fontSize: 'inherit' }}
            >
              {formatPeriodLabel(previous.start_date, previous.end_date)}
            </button>
          )}
          {current && (
            <button
              className={`${styles.navArrow} ${pay_period.start_date === current.start_date ? styles.navArrowActive : ''}`}
              onClick={() => { setSelectedStartDate(current.start_date); setExpandedGroups(new Set()) }}
              style={{ width: 'auto', padding: '4px 12px', fontSize: 'inherit' }}
            >
              {formatPeriodLabel(current.start_date, current.end_date)}
            </button>
          )}
          <span className={getStateBadgeClass(pay_period.state)}>{capitalize(pay_period.state)}</span>
        </div>
      ) : (
        <div className={styles.periodNav}>
          <button className={styles.navArrow} onClick={handlePrev} disabled={!canGoBack} aria-label="Previous period">
            &#8249;
          </button>
          <div className={styles.periodLabel}>
            <span>{formatPeriodLabel(pay_period.start_date, pay_period.end_date)}</span>
            <span className={getStateBadgeClass(pay_period.state)}>{capitalize(pay_period.state)}</span>
          </div>
          <button className={styles.navArrow} onClick={handleNext} disabled={!canGoForward} aria-label="Next period">
            &#8250;
          </button>
        </div>
      )}

      {/* Payout date */}
      {pay_period.payout_date && (
        <div className={styles.periodLabel}>
          <span>Payout: {new Date(pay_period.payout_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        </div>
      )}

      {/* Summary cards */}
      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{summary.total}</div>
          <div className={styles.summaryLabel}>Total Employees</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{summary.submitted}</div>
          <div className={styles.summaryLabel}>Submitted</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{summary.approved}</div>
          <div className={styles.summaryLabel}>Approved</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryValue}>{needsAction}</div>
          <div className={styles.summaryLabel}>Needs Action</div>
        </div>
      </div>

      {renderActions && renderActions(pay_period)}

      {/* Employee status table */}
      {employees.length > 0 ? (
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Employee</th>
              <th className={styles.th}>Department</th>
              <th className={styles.th}>Status</th>
              <th className={styles.th}>Hours</th>
            </tr>
          </thead>
          <tbody>
            {managerGroups
              ? managerGroups.map((group) => {
                  const isExpanded = expandedGroups.has(group.managerId)
                  const groupKey = group.managerId ?? 'no-manager'
                  return (
                    <GroupRows
                      key={groupKey}
                      group={group}
                      isExpanded={isExpanded}
                      onToggle={() => toggleGroup(group.managerId)}
                      onEmployeeClick={onEmployeeClick}
                    />
                  )
                })
              : employees.map((emp) => (
                  <tr
                    key={emp.employee_id}
                    onClick={onEmployeeClick ? () => onEmployeeClick(emp) : undefined}
                    style={onEmployeeClick ? { cursor: 'pointer' } : undefined}
                  >
                    <td className={styles.td}>{emp.employee_name}</td>
                    <td className={styles.td}>{emp.department ?? '\u2014'}</td>
                    <td className={styles.td}>
                      <span className={getStatusBadgeClass(emp.status)}>{emp.status}</span>
                    </td>
                    <td className={styles.td}>{emp.total_hours}h</td>
                  </tr>
                ))}
          </tbody>
        </table>
      ) : (
        <div className={styles.loading}>No employees found for this period.</div>
      )}
    </div>
  )
}

function GroupRows({
  group,
  isExpanded,
  onToggle,
  onEmployeeClick,
}: {
  group: ManagerGroup
  isExpanded: boolean
  onToggle: () => void
  onEmployeeClick?: (emp: EmployeeTimesheetStatus) => void
}) {
  return (
    <>
      <tr className={styles.managerRow} onClick={onToggle}>
        <td className={styles.managerTd} colSpan={4}>
          <span className={styles.expandIcon}>{isExpanded ? '\u25BE' : '\u25B8'}</span>
          {group.managerName} ({group.employees.length})
          <span className={styles.groupCount}>{formatGroupCounts(group.counts)}</span>
        </td>
      </tr>
      {isExpanded &&
        group.employees.map((emp) => (
          <tr
            key={emp.employee_id}
            className={styles.childRow}
            onClick={onEmployeeClick ? () => onEmployeeClick(emp) : undefined}
            style={onEmployeeClick ? { cursor: 'pointer' } : undefined}
          >
            <td className={styles.tdIndented}>{emp.employee_name}</td>
            <td className={styles.td}>{emp.department ?? '\u2014'}</td>
            <td className={styles.td}>
              <span className={getStatusBadgeClass(emp.status)}>{emp.status}</span>
            </td>
            <td className={styles.td}>{emp.total_hours}h</td>
          </tr>
        ))}
    </>
  )
}
